import { Button, Input, Skeleton, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Field } from '../../components/field';
import { SectionContainer, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import type { Company } from './companies.api';
import {
  documentNumberingQueryKey,
  documentNumberingQueryOptions,
  saveDocumentNumbering,
} from './document-numbering.api';

/**
 * „Registrul de documente" din setările firmei: de la ce număr numerotează CRM-ul
 * ([#239](https://github.com/bplugaru/efx-pcrm/issues/239)).
 *
 * Fondatorul, 2026-10-22: *„pentru generat automat o să fie o valoare setată. numerele manuale
 * se introduc până la acea valoare. după ce este automat"*. Contractele din sistemul vechi se
 * încarcă cu numărul lor, scris de mână, **sub** prag; tot ce numerotează CRM-ul pornește de la
 * prag. Cele două șiruri nu se pot întâlni.
 *
 * **Se salvează pe loc**, ca seria de facturi: pragul stă pe contorul registrului, nu pe rândul
 * firmei.
 */
export function DocumentNumberingSection({ company }: { company: Company }) {
  const queryClient = useQueryClient();
  const query = useQuery(documentNumberingQueryOptions(company.id));
  const current = query.data ?? null;

  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(current ? String(current.nextNumber) : '');
    setError(null);
  }, [current]);

  const save = useMutation({
    mutationFn: (nextNumber: number) =>
      saveDocumentNumbering({ companyId: company.id, nextNumber }),
    onSuccess: async (saved) => {
      toast.success(`Următorul document numerotat automat va lua numărul ${saved.nextNumber}`);
      await queryClient.invalidateQueries({ queryKey: documentNumberingQueryKey(company.id) });
    },
    onError: (failure: unknown) => {
      if (failure instanceof ApiError) {
        const message =
          failure.code === 'document_numbering_too_low'
            ? failure.message
            : failure.issueFor('nextNumber');

        if (message !== undefined) {
          setError(message);
          return;
        }
      }

      toast.error(
        failure instanceof ApiError ? failure.message : 'Numerotarea nu a putut fi salvată.',
      );
    },
  });

  const nextNumber = /^[1-9][0-9]*$/.test(value) ? Number(value) : null;
  const changed = current !== null && nextNumber !== current.nextNumber;
  const canSave = !save.isPending && changed && nextNumber !== null;

  return (
    <SectionContainer>
      <SectionHeader
        title="Registrul de documente"
        subtitle="Numerele mai mici rămân pentru actele aduse din sistemul vechi, scrise de mână la încărcare."
      />
      <div className="flex max-w-2xl flex-col gap-y-4 px-6 py-4">
        {query.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : query.error ? (
          <Text size="small" className="text-ui-fg-error">
            {query.error instanceof Error
              ? query.error.message
              : 'Numerotarea nu a putut fi citită.'}
          </Text>
        ) : (
          <>
            <Field
              label="Următorul număr automat"
              id="document-numbering-next"
              error={error ?? undefined}
              hint={
                query.data.highestNumber === null
                  ? 'Registrul firmei e gol.'
                  : `Cel mai mare număr din registru: ${query.data.highestNumber}.`
              }
            >
              {/* `type="text"`, ca la seria de facturi: rotița mouse-ului nu mută pragul. */}
              <Input
                id="document-numbering-next"
                inputMode="numeric"
                value={value}
                onChange={(event) => {
                  setValue(event.target.value.trim());
                  setError(null);
                }}
                className="max-w-48 font-mono tabular-nums"
                disabled={save.isPending}
              />
            </Field>

            <div>
              <Button
                size="small"
                variant="secondary"
                onClick={() => nextNumber !== null && save.mutate(nextNumber)}
                isLoading={save.isPending}
                disabled={!canSave}
              >
                Salvează numerotarea
              </Button>
            </div>
          </>
        )}
      </div>
    </SectionContainer>
  );
}
